import { ReactNode, useState } from "react";
import { ImpactData, LifecycleStage } from "../../screens/productSummary/types";
import { Card, CardContent } from "../ui/common/card";
import { TooltipProvider } from "../ui/common/tooltip";
import ClimateImpactCard from "./ClimateImpactCard";
import LifecycleStagesCard from "./LifecycleStagesCard";
import ProductInfoCard from "./ProductInfoCard";
import { ImpactCardsGrid } from "../impactCardsGrid/ImpactCardsGrid";

interface EnvironmentalFootprintCardsProps {
  productImage?: string;
  productName?: string;
  /** Muted subtitle under the name — e.g. the supplier name for an ingredient. */
  productSubtitle?: string;
  calculatedDate: string;
  isAverageProduct: boolean;
  climateChangeMeasure?: number;
  climateChangeUnit?: string;
  /** Noun after "/kg" on the climate impact card — "ingredient" vs "product" (default). */
  basisLabel?: string;
  lifecycleStages: LifecycleStage[];
  totalImpact: ImpactData[];
  /** Roomier padding/bolder titles for the tab-in-modal contexts (product/ingredient details). */
  compact?: boolean;
  /** Wrap the impact-category grid in its own white Card, so it reads as a
   * distinct section against a modal's off-white background. */
  wrapImpactGrid?: boolean;
  /** Optional content rendered between the top card row and the impact-category grid. */
  middleContent?: ReactNode;
}

/**
 * The "Environmental Footprint" card layout shared by the Product Summary
 * page and the Product/Ingredient details modals' Environmental Footprint
 * tabs: product/ingredient info + climate impact side by side, lifecycle
 * stage breakdown, then the impact-category grid. Each caller keeps its own
 * data-fetching/loading/error handling; this only owns the shared visual
 * composition (including the expand/collapse state for lifecycle stages).
 */
const EnvironmentalFootprintCards = ({
  productImage,
  productName,
  productSubtitle,
  calculatedDate,
  isAverageProduct,
  climateChangeMeasure,
  climateChangeUnit,
  basisLabel,
  lifecycleStages,
  totalImpact,
  compact,
  wrapImpactGrid,
  middleContent
}: EnvironmentalFootprintCardsProps) => {
  const [expandedStage, setExpandedStage] = useState<string | null>(null);

  const impactGrid = <ImpactCardsGrid impactData={totalImpact} />;

  return (
    <TooltipProvider>
      <div className="tw-flex tw-flex-col tw-gap-5">
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12 tw-gap-5 tw-items-stretch">
          <div className="lg:tw-col-span-6 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-5">
            <ProductInfoCard
              productImage={productImage}
              productName={productName}
              productSubtitle={productSubtitle}
              calculatedDate={calculatedDate}
              isAverageProduct={isAverageProduct}
              compact={compact}
            />
            <ClimateImpactCard
              measure={climateChangeMeasure}
              unit={climateChangeUnit}
              basisLabel={basisLabel}
            />
          </div>

          <LifecycleStagesCard
            stages={lifecycleStages}
            expandedStage={expandedStage}
            onToggleStage={stage => setExpandedStage(stage || null)}
            compact={compact}
          />
        </div>

        {middleContent}

        {wrapImpactGrid ? (
          <Card className="tw-border-border tw-bg-white tw-p-1 tw-rounded-lg">
            <CardContent className="tw-p-2">{impactGrid}</CardContent>
          </Card>
        ) : (
          impactGrid
        )}
      </div>
    </TooltipProvider>
  );
};

export default EnvironmentalFootprintCards;
